import {
  getOrderConfirmationFormat,
  isSupportedOrderConfirmationFormat,
  resolveOrderConfirmationFormat,
  SUPPORTED_ORDER_CONFIRMATION_FORMAT_IDS,
} from '../config/orderConfirmationFormats';
import {
  SIGMA_MATERIAH_SUPPLIER_PRIMARY,
  SIGMA_ORDER_CONFIRMATION_BRAND,
} from '../config/orderConfirmationFormats';
import { logInfo, logWarn } from '../config_and_helpers/logger';

const LOG_CONTEXT = 'orderConfirmationFormatValidation';

/**
 * @typedef {Object} OrderConfirmationFormatContextResult
 * @property {boolean} canProceed
 * @property {string[]} blockingErrors
 * @property {import('../types/orderConfirmationExtractionTypes').ExtractionWarning[]} warnings
 * @property {{ formatId: string, displayName: string }|null} format
 * @property {{ value: unknown, label: string, matchReason: string }|null} suggestedSupplier
 */

/**
 * Validate extraction parser format and suggest Materiah supplier by name match.
 *
 * @param {import('../types/orderConfirmationExtractionTypes').ExtractedOrderConfirmationData} extractedData
 * @param {Array<{ value: unknown, label?: string }>} [supplierOptions]
 * @returns {OrderConfirmationFormatContextResult}
 */
export function validateExtractedOrderConfirmationContext(
  extractedData,
  supplierOptions = []
) {
  const blockingErrors = [];
  const warnings = [];
  const { format, formatId } = resolveOrderConfirmationFormat(extractedData);

  if (!formatId) {
    blockingErrors.push(
      'Could not identify the order confirmation format. This CSV may be from an unsupported supplier.'
    );
    logWarn(LOG_CONTEXT, 'missing parserFormat in extraction meta', {});
    return {
      canProceed: false,
      blockingErrors,
      warnings,
      format: null,
      suggestedSupplier: null,
    };
  }

  if (!format || !format.supported) {
    blockingErrors.push(
      `Unsupported order confirmation format (“${formatId}”). Contact an administrator to enable this supplier.`
    );
    logWarn(LOG_CONTEXT, 'unsupported parserFormat', { formatId });
    return {
      canProceed: false,
      blockingErrors,
      warnings,
      format: null,
      suggestedSupplier: null,
    };
  }

  if (!SUPPORTED_ORDER_CONFIRMATION_FORMAT_IDS.includes(format.formatId)) {
    blockingErrors.push(
      `Order confirmation format “${formatId}” is not enabled in this application.`
    );
    return {
      canProceed: false,
      blockingErrors,
      warnings,
      format: null,
      suggestedSupplier: null,
    };
  }

  const extractedSupplier = extractedData?.supplier || {};
  if (!format.verifyFingerprints(extractedSupplier)) {
    warnings.push({
      field: 'meta.parserFormat',
      message: `Extracted supplier details do not fully match ${format.displayName}. Please verify before saving.`,
      severity: 'warning',
    });
    logWarn(LOG_CONTEXT, 'fingerprint mismatch', {
      formatId: format.formatId,
      supplierName: extractedSupplier.name ?? null,
    });
  }

  const isSigmaCsv = format.formatId === 'sigma_aldrich_order_confirmation_csv';
  if (isSigmaCsv) {
    logInfo(LOG_CONTEXT, 'Sigma-Aldrich CSV recognized — matching Materiah supplier Merck', {
      fileSupplierName: extractedSupplier.name ?? null,
      recognizedBrand: extractedSupplier.recognizedBrand ?? SIGMA_ORDER_CONFIRMATION_BRAND,
      materiahHint: extractedSupplier.materiahSupplierHint ?? SIGMA_MATERIAH_SUPPLIER_PRIMARY,
      supplierOptionCount: supplierOptions.length,
      supplierLabelsSample: supplierOptions.slice(0, 15).map(o => o.label),
    });
  }

  const match = format.matchMateriahSupplierOption(
    supplierOptions,
    extractedSupplier
  );

  if (match) {
    logInfo(LOG_CONTEXT, 'Materiah supplier matched', {
      formatId: format.formatId,
      supplierId: match.value,
      supplierLabel: match.label,
      matchReason: match.matchReason,
      score: match.score,
      sigmaToMerck: isSigmaCsv && /merck/i.test(String(match.label)),
    });
  } else {
    const hint =
      isSigmaCsv && /sigma|aldrich/i.test(String(extractedSupplier.name || ''))
        ? ` This file is from ${SIGMA_ORDER_CONFIRMATION_BRAND}; select “${SIGMA_MATERIAH_SUPPLIER_PRIMARY}” if listed.`
        : '';
    warnings.push({
      field: 'supplier',
      message: `No Materiah supplier matched the ${format.displayName} name on this file. Select the supplier manually.${hint}`,
      severity: 'warning',
    });
    logWarn(LOG_CONTEXT, 'no Materiah supplier match', {
      formatId: format.formatId,
      extractedName: extractedSupplier.name ?? null,
      recognizedBrand: extractedSupplier.recognizedBrand ?? null,
      materiahHint: extractedSupplier.materiahSupplierHint ?? null,
      supplierOptionCount: supplierOptions.length,
    });
  }

  return {
    canProceed: true,
    blockingErrors,
    warnings,
    format: { formatId: format.formatId, displayName: format.displayName },
    suggestedSupplier: match
      ? {
          value: match.value,
          label: match.label,
          matchReason: match.matchReason,
        }
      : null,
  };
}

export {
  getOrderConfirmationFormat,
  isSupportedOrderConfirmationFormat,
  resolveOrderConfirmationFormat,
};
